import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { LucideIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

interface StatCardProps {
  title: string;
  value: string | number;
  description: string;
  icon: LucideIcon;
  trend?: {
    data: Array<{ name: string; value: number }>;
    description: string;
  };
  details?: {
    items: Array<{ label: string; value: string | number }>;
    description?: string;
  };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  return (
    <div className="glass-effect rounded-lg border border-opacity-20 p-3 shadow-sm">
      <div className="text-sm font-semibold">{label}</div>
      <div className="text-sm text-muted-foreground">
        {payload[0].value}
      </div>
    </div>
  );
}

export function InteractiveStatCard({ 
  title, 
  value, 
  description, 
  icon: Icon,
  trend,
  details 
}: StatCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Card 
        variant="glass"
        className="overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1 duration-300 cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-primary/10 p-3 flex items-center justify-center">
              <Icon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground/80">
                {title}
              </p>
              <div className="text-2xl font-bold tracking-tight text-primary">{value}</div>
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            </div>
          </div>

          {trend && (
            <div className="h-[60px] mt-4 opacity-75">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend.data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="h-5 w-5 text-primary" />
              {title}
            </DialogTitle>
            <DialogDescription>
              Detailed information and trends
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <Card variant="glass" className="overflow-hidden">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-primary">{value}</div>
                <p className="text-sm text-muted-foreground mt-1">{description}</p>
              </CardContent>
            </Card>

            {trend && (
              <div className="space-y-3">
                <h3 className="font-medium text-lg">Trend Analysis</h3>
                <Card variant="glass" className="p-4">
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trend.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <XAxis
                          dataKey="name"
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(value) => `${value}`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          dot={{
                            stroke: "hsl(var(--primary))",
                            strokeWidth: 2,
                            fill: "hsl(var(--background))",
                            r: 4,
                          }}
                          activeDot={{
                            r: 6,
                            stroke: "hsl(var(--primary))",
                            strokeWidth: 2,
                            fill: "hsl(var(--primary))",
                          }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    {trend.description}
                  </p>
                </Card>
              </div>
            )}

            {details && (
              <div className="space-y-3">
                <h3 className="font-medium text-lg">Detailed Breakdown</h3>
                {details.description && (
                  <p className="text-sm text-muted-foreground mb-2">
                    {details.description}
                  </p>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  {details.items.map((item, index) => (
                    <Card key={index} variant="glass" className="overflow-hidden">
                      <CardContent className="p-4">
                        <p className="text-sm text-foreground/80">{item.label}</p>
                        <p className="text-xl font-semibold mt-1 text-primary">{item.value}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}